// YouTube chapter preview for the Episode Editor.
//
// Mirrors lambda/episode-processor/handler.py (_collect_chapter_markers,
// _normalise_markers, _hhmmss) rule for rule, so what the editor previews is
// exactly what the ['chapters'] job will splice into the YouTube description.
// If you change one, change the other.

export const YT_MIN_CHAPTERS = 3;
export const YT_MIN_CHAPTER_SEC = 10;

// YouTube chapter timestamp: MM:SS under an hour, H:MM:SS over.
export function chapterTime(sec) {
  sec = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function storyLabel(title, who) {
  if (title && who) return `Story — ${title} | ${who}`;
  if (title) return `Story — ${title}`;
  if (who) return `Story — ${who}`;
  return "Story";
}

const clean = (s) => String(s || "").split(/\s+/).filter(Boolean).join(" ");

/**
 * @param episode      episodes row ({host_monologue_start})
 * @param roster       episode_storytellers rows, any order
 *                     ({storyteller_id, sort_order, intro_start, discussion_start})
 * @param performances performances rows ({storyteller_id, story_id,
 *                     story_start_sec, discussion_start_sec, storyteller_name_*})
 * @param stories      {story_id: {title_telugu}}
 * @param names        {storyteller_id: display name}
 * @returns raw markers [{sec, label, source}] — unsorted, like the Python
 */
export function collectMarkers({ episode, roster, performances, stories, names }) {
  const markers = [];
  const add = (sec, label, source) => {
    if (sec === null || sec === undefined || sec === "") return;
    const n = Math.trunc(Number(sec));
    if (!Number.isFinite(n) || n < 0) return;
    const l = clean(label);
    if (l) markers.push({ sec: n, label: l, source });
  };

  const nameOf = (sid) => {
    if (names?.[sid]) return names[sid].trim();
    const p = performances.find((x) => x.storyteller_id === sid);
    return clean(p?.storyteller_name_telugu || p?.storyteller_name_english || "");
  };

  add(episode?.host_monologue_start, "Introduction", "host");

  const firstPerf = {};
  performances.forEach((p) => {
    if (p.storyteller_id && !firstPerf[p.storyteller_id]) firstPerf[p.storyteller_id] = p;
  });

  const used = new Set();
  [...roster].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).forEach((r) => {
    const sid = r.storyteller_id;
    const who = nameOf(sid);
    const p = firstPerf[sid] || null;
    if (p) used.add(p);
    add(r.intro_start, who ? `Storyteller intro — ${who}` : "Storyteller intro", `intro:${sid}`);
    const title = clean(stories?.[p?.story_id]?.title_telugu || "");
    if (p) add(p.story_start_sec, storyLabel(title, who), `story:${p.performance_id}`);
    const disc = r.discussion_start ?? p?.discussion_start_sec;
    add(disc, who ? `Discussion — ${who}` : "Discussion", `discussion:${sid}`);
  });

  performances.forEach((p) => {
    if (used.has(p)) return;
    // Python: names.get(p.get("storyteller_id"), "") — no id, no name.
    const who = p.storyteller_id ? nameOf(p.storyteller_id) : "";
    const title = clean(stories?.[p.story_id]?.title_telugu || "");
    add(p.story_start_sec, storyLabel(title, who), `story:${p.performance_id}`);
    add(p.discussion_start_sec, who ? `Discussion — ${who}` : "Discussion", `discussion-perf:${p.performance_id}`);
  });

  return markers;
}

/**
 * Sort, force a 00:00 opener, and drop anything within YouTube's 10-second
 * minimum of the previous kept marker. Returns {chapters, dropped} so the
 * editor can say *why* a marker will not appear.
 */
export function normaliseMarkers(markers) {
  // Stable sort by seconds (Python's sorted() is stable too).
  const ordered = markers.map((m, i) => ({ ...m, _i: i }))
    .sort((a, b) => a.sec - b.sec || a._i - b._i)
    .map(({ _i, ...m }) => m);
  if (!ordered.length) return { chapters: [], dropped: [] };

  if (ordered[0].sec > 0) {
    if (ordered[0].label.startsWith("Introduction")) ordered[0] = { ...ordered[0], sec: 0 };
    else ordered.unshift({ sec: 0, label: "Introduction", source: "auto-opener" });
  }

  const chapters = [];
  const dropped = [];
  ordered.forEach((m) => {
    const prev = chapters[chapters.length - 1];
    if (prev && m.sec - prev.sec < YT_MIN_CHAPTER_SEC) dropped.push({ ...m, reason: `within ${YT_MIN_CHAPTER_SEC}s of "${prev.label}"` });
    else chapters.push(m);
  });
  return { chapters, dropped };
}

export function chapterBlockLines(chapters) {
  return chapters.map((m) => `${chapterTime(m.sec)} ${m.label}`);
}

// Per-story transcript slice — mirrors pipeline/kathakala/transcriber.py
// slice_transcript: every segment whose START lies in [start, end], joined
// with single spaces. segments are the transcribe run's {start_sec, text}.
export function sliceTranscript(segments, startSec, endSec) {
  if (startSec == null || endSec == null || endSec <= startSec) return "";
  return (segments || [])
    .map((s) => ({ t: Math.trunc(Number(s.start_sec ?? s.start ?? 0)), text: String(s.text || "").trim() }))
    .sort((a, b) => a.t - b.t)
    .filter((s) => s.t >= startSec && s.t <= endSec && s.text)
    .map((s) => s.text)
    .join(" ")
    .trim();
}
